import React, { useState, useEffect } from "react";
import { BookOpen, MessageCircle, Clock, Download } from "lucide-react";
import { getAllAgents } from "../services/AgentService";
import { getAllEnrollments } from "../services/EnrolmentService";
import { getAllEnquiries } from "../services/EnquiryService";
import { getAllStudents } from "../services/StudentRecordService";
import ViewEnrolmentForm from "./forms/EnrolmentForms/ViewEnrolmentForm";
import EnquiryForm from "./forms/EnquiryForm";
import StudentRecordForm from "./forms/StudentForms/StudentRecordForm";
import {StatusBadge} from "./SharedInputs";
import ViewStudentRecordForm from "./forms/StudentForms/ViewStudentRecordForm";
// import AgentInternalForm from "./forms/AgentInternalForm";

import "../css/Dashboard.css";

const DashboardReport = () => {
  const [selectedCard, setSelectedCard] = useState("enrolments");
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [enrolments, setEnrolments] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [prism, setPrismDue] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "progress", direction: "desc" });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [feeDueTab, setFeeDueTab] = useState("past");

  // -----------------------------
  // Fetch data from services
  // -----------------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [enrolmentsData, enquiriesData, prismData, agentsData] =
          await Promise.all([
            getAllEnrollments(),
            getAllEnquiries(),
            getAllStudents(),
            getAllAgents(),
          ]);

        setEnrolments(enrolmentsData || []);
        setEnquiries(enquiriesData || []);
        setPrismDue(prismData || []);
        setAgents(agentsData || []);
      } catch (err) {
        alert("Error fetching dashboard data");
      }
    };

    fetchData();
  }, []);

  // -----------------------------
  // Filtered data helpers
  // -----------------------------
  const getNewEnrolments = () => enrolments.filter((e) => e.status === "pending");
  const getNewEnquiries = () => enquiries.filter((e) => e.status === "new");

  const getPrismDueSoon = () => {
    const today = new Date();

    return prism.flatMap((student) =>
      (student.formState.courses || [])
        .filter((course) => {
          const validStatuses = [
          "Ongoing",
          "Upcoming - Awaiting Visa (COE) or Course not Started (VOE/Onshore)",
          "Waiting for Visa"
          ];

          if (!validStatuses.includes(student.formState?.studentStatus)) {
            return false;
          }
          if ((course.prismStatus !== "" && (course.prismStatus !== "Pending" && course.prismStatus !== "Reported")) || (course.coeStatus == "Withdrawn" || course.coeStatus == "Cancelled" || course.decision == "Refused")) return false;

          const dueDate = new Date(course.courseStartDate);
          const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);

          const duePrismDate = new Date(course.prismsDueDate);
          const diffPrismDays = (duePrismDate - today) / (1000 * 60 * 60 * 24);

          return (diffDays >= 0 && diffDays <= 6) || (diffPrismDays >= 0 && diffPrismDays <= 6) || diffDays < 0 || diffPrismDays < 0; // due in next 5 days
        })
        .map((course) => {
          const dueDate = new Date(course.courseStartDate);
          const diffDays = Math.round((dueDate - today) / (1000 * 60 * 60 * 24));

          return {
            id: student,
            studentId: student.formState.studentId,
            studentName: `${student.formState.firstName} ${student.formState.lastName}`,
            studentDateOfBirth: student.formState.dateOfBirth,
            courseName: course.courseName,
            courseStartDate: course.courseStartDate,
            coeNo: course.coeNumber,
            revisedCoeNo: course.revisedCoeNo || "",
            coeIssuedDate: course.coeIssuedDate,
            prismsDueDate: `${course.prismsDueDate} (${diffDays} days)`,
            prismStatus: course.prismStatus,
          };
        })
    );
  };
  
  function extractAllFeePlans(student) {
    const feeUnits = [];
    const courses = student?.formState?.courses || [];
    const packages = student?.formState?.packages || [];
    const coeType = student?.formState?.feePlanType; // "Package" or "Independent"
    const campus = student?.formState?.campusName || "";
    const studentName = `${student?.formState?.firstName} ${student?.formState?.lastName}`;
    
    // ------------------------------
    // 🟦 MODE 1: INDEPENDENT (courses-based)
    // ------------------------------
    if (coeType !== "Package") {
      courses.forEach(course => {
        const fees = course.feesPlan;
        if (!fees) return;

        const courseName = course.courseName || "Course";

        // 1. Initial Deposit from course
        if (fees.initialDeposit?.dueDate) {
          feeUnits.push({
            studentName,
            campus,
            courseName,
            type: "Initial Deposit",
            dueDate: fees.initialDeposit.dueDate,
            amount: parseFloat(fees.initialDeposit.depositAmount || 0),
            amountPaid: parseFloat(fees.initialDeposit.amountPaid || 0),
            status: fees.initialDeposit.status || "Pending",
            invoiceNumber: fees.initialDeposit.invoiceNumber || "",
            invoiceSent: fees.initialDeposit.invoiceSent === true || !!fees.initialDeposit.invoiceNumber,
            invoiceSentDate: fees.initialDeposit.invoiceSentDate || "",
          });
        }

        // 2. Installments from course
        (fees.installments || []).forEach((inst, index) => {
          if (!inst.dueDate) return; // Skip if no due date

          feeUnits.push({
            studentName,
            campus,
            courseName,
            type: `Installment ${index + 1}`,
            dueDate: inst.dueDate,
            amount: parseFloat(inst.depositAmount || 0),
            amountPaid: parseFloat(inst.amountPaid || 0),
            status: inst.status || "Pending",
            invoiceNumber: inst.invoiceNumber || "",
            invoiceSent: inst.invoiceSent === true || !!inst.invoiceNumber,
            invoiceSentDate: inst.invoiceSentDate || "",
          });
        });

        // 3. Legacy feePlan array (backward compatibility)
        (course.feePlan || []).forEach((fp, index) => {
          if (!fp.dueDate) return;
          feeUnits.push({
            studentName,
            campus,
            courseName,
            type: `Payment ${index + 1}`,
            dueDate: fp.dueDate,
            amount: parseFloat(fp.amount || 0),
            amountPaid: parseFloat(fp.amountPaid || 0),
            status: fp.status || "Pending",
            invoiceNumber: fp.invoiceNumber || "",
            invoiceSent: fp.invoiceSent === true || !!fp.invoiceNumber,
            invoiceSentDate: fp.invoiceSentDate || "",
          });
        });
      });
    }

    // ------------------------------
    // 🟦 MODE 2: PACKAGE (packages-based)
    // ------------------------------
    if (coeType === "Package") {
      packages.forEach(pkg => {
        const fees = pkg.feesPlan;
        if (!fees) return;

        const pkgName = pkg.name || "Package";

        // 1. Initial Deposit from package
        if (fees.initialDeposit?.dueDate) {
          feeUnits.push({
            studentName,
            campus,
            courseName: pkgName,
            type: "Initial Deposit",
            dueDate: fees.initialDeposit.dueDate,
            amount: parseFloat(fees.initialDeposit.depositAmount || 0),
            amountPaid: parseFloat(fees.initialDeposit.amountPaid || 0),
            status: fees.initialDeposit.status || "Pending",
            invoiceNumber: fees.initialDeposit.invoiceNumber || "",
            invoiceSent: fees.initialDeposit.invoiceSent === true || !!fees.initialDeposit.invoiceNumber,
            invoiceSentDate: fees.initialDeposit.invoiceSentDate || "",
          });
        }

        // 2. Installments from package
        (fees.installments || []).forEach((inst, index) => {
          if (!inst.dueDate) return; // Skip if no due date

          feeUnits.push({
            studentName,
            campus,
            courseName: pkgName,
            type: `Installment ${index + 1}`,
            dueDate: inst.dueDate,
            amount: parseFloat(inst.depositAmount || 0),
            amountPaid: parseFloat(inst.amountPaid || 0),
            status: inst.status || "Pending",
            invoiceNumber: inst.invoiceNumber || "",
            invoiceSent: inst.invoiceSent === true || !!inst.invoiceNumber,
            invoiceSentDate: inst.invoiceSentDate || "",
          });
        });
      });
    }

    // Sort by due date
    return feeUnits.sort((a, b) => {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate) - new Date(b.dueDate);
    });
  }

  function getFeeInstallmentsDue() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Same style as Starting Soon:
    // show fees overdue in last 10 days OR due in next 10 days
    const pastLimit = -999999;
    const futureLimit = 15;

    return prism
      .map((student) => {
        const validStatuses = [
          "New Enrolment",
          "Ongoing",
          "Upcoming - Awaiting Visa (COE) or Course not Started (VOE/Onshore)",
        ];

        if (!validStatuses.includes(student.formState?.studentStatus)) {
          return null;
        }

        const rawFees = extractAllFeePlans(student);

        if (!rawFees || rawFees.length === 0) {
          return null;
        }

        const fees = rawFees.map((unit) => {
          const dueDate = new Date(unit.dueDate);
          dueDate.setHours(0, 0, 0, 0);

          const diffDays = Math.round(
            (dueDate - today) / (1000 * 60 * 60 * 24)
          );

          const amountDue = unit.amount || 0;
          const amountPaid = unit.amountPaid || 0;
          const remainingOnThis = amountDue - amountPaid;

          return {
            ...unit,
            daysRemaining: isNaN(diffDays) ? 9999 : diffDays,
            amountDue,
            amountPaid,
            remainingOnThis,
            isPending: remainingOnThis > 0,
            isOverdue: remainingOnThis > 0 && diffDays < 0,
          };
        });

        // IMPORTANT:
        // Only show unpaid fees that are within the due window.
        // No fallback to all unpaid fees.
        const dueFees = fees.filter(
          (f) =>
            f.isPending &&
            f.daysRemaining >= pastLimit &&
            f.daysRemaining <= futureLimit
        );

        if (dueFees.length === 0) {
          return null;
        }

        const totalFees = rawFees.reduce((sum, f) => sum + (f.amount || 0), 0);
        const totalPaid = rawFees.reduce((sum, f) => sum + (f.amountPaid || 0), 0);

        const overdueFees = dueFees.filter((f) => f.isOverdue);
        const pendingBalance = dueFees.reduce(
          (sum, f) => sum + f.remainingOnThis,
          0
        );

        const upcoming = dueFees
          .filter((f) => f.daysRemaining >= 0)
          .sort((a, b) => a.daysRemaining - b.daysRemaining);

        const overdueList = dueFees
          .filter((f) => f.isOverdue)
          .sort((a, b) => b.daysRemaining - a.daysRemaining);

        const next = overdueList[0] || upcoming[0] || dueFees[0];

        const paidFees = fees.filter((f) => !f.isPending);

        const lastPaid =
          paidFees.length > 0
            ? paidFees.sort(
                (a, b) => new Date(b.dueDate) - new Date(a.dueDate)
              )[0]
            : null;

        let lastInstallmentDays = null;

        if (lastPaid?.dueDate) {
          const lastDate = new Date(lastPaid.dueDate);
          lastDate.setHours(0, 0, 0, 0);

          lastInstallmentDays = Math.floor(
            (today - lastDate) / (1000 * 60 * 60 * 24)
          );
        }

        return {
          id: student,
          studentName: `${student.formState.firstName} ${student.formState.lastName}`,
          studentId: student.formState.studentId || "",
          campus: student.formState.campusName || "N/A",
          coeType: student.formState.coeType || "Independent",
          courseName: next?.courseName || "-",

          totalFees,
          totalPaid,

          // Now this is only balance for dues in the dashboard window
          pendingBalance,

          lastInstallmentAmount: lastPaid?.amountPaid || 0,
          lastInstallmentDate: lastPaid?.dueDate || "-",
          lastInstallmentDays,
          lastInstallmentStatus: lastPaid ? "Paid" : "-",

          nextInstallmentDate: next?.dueDate || "-",
          nextAmount: next?.amountDue || 0,
          nextAmountRemaining: next?.remainingOnThis || 0,
          status: next?.status || "Unpaid",
          daysRemaining: next?.daysRemaining ?? 9999,
          invoiceNumber: next?.invoiceNumber || "",
          invoiceSent: next?.invoiceSent === true || !!next?.invoiceNumber,
          invoiceSentDate: next?.invoiceSentDate || "",

          overdueCount: overdueFees.length,
          overdueAmount: overdueFees.reduce(
            (sum, f) => sum + f.remainingOnThis,
            0
          ),

          paymentProgress: totalFees > 0 ? (totalPaid / totalFees) * 100 : 0,

          dueLabel:
            next?.daysRemaining < 0
              ? `${Math.abs(next.daysRemaining)} day${
                  Math.abs(next.daysRemaining) > 0 ? "s" : ""
                } overdue`
              : next?.daysRemaining === 0
              ? "Due Today"
              : `Due in ${next.daysRemaining} day${
                  next.daysRemaining > 0 ? "s" : ""
                }`,

          _studentRef: student,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.daysRemaining - b.daysRemaining);
  }




  const getAgentProgress = () => {
    const filteredAgents = agents.filter(agent =>
          !agent.agentName.toLowerCase().includes("test") && !agent.agentName.toLowerCase().includes("suspended")
        );
    return filteredAgents.map((agent) => {
      const agentName = agent.agentName || "Unknown Agent";
      const agentEmail = agent.email || "";
      const agentPhone = agent.phone || "";
      const agentAddress = agent.address || ""; 
      const agentCode = agent.agentCode || "";

          // Get list of all students for this agent
      const studentsForAgent = prism.filter(
        (student) =>
          student.formState?.agentName?.toLowerCase?.() ===
          agent.agentName?.toLowerCase()
      );

      // Calculate cutoff date = 3 months ago
      const now = new Date();
      const cutoffDate = new Date();
      cutoffDate.setMonth(now.getMonth() - 3);

      // Count students created in the last 3 months
      const recentStudents = studentsForAgent.filter((s) => {
        const createdAt = s.createdAt?.toDate
          ? s.createdAt.toDate()
          : new Date(s.createdAt);
        return createdAt >= cutoffDate;
      });

      // Compute progress — recent students as % of total
      const progress =
        studentsForAgent.length === 0
          ? 0
          : Math.round((recentStudents.length / studentsForAgent.length) * 100);



      return {
        agentCode,
        agentName,
        agentEmail,
        agentPhone,
        agentStatus: agent.status ? agent.status.charAt(0).toUpperCase() + agent.status.slice(1).toLowerCase() : "Unknown",
        agentAddress,
        totalStudents: studentsForAgent.length,
        recentStudents: recentStudents.length,
        progress: progress,
      };
    });
  };
  const SortIcon = ({ direction }) => {
    if (!direction) return <span style={{ marginLeft: 4 }}>⇅</span>; // unsorted
    return direction === "asc" ? <span style={{ marginLeft: 4 }}>↑</span> : <span style={{ marginLeft: 4 }}>↓</span>;
  };

  const getLowProgressAgents = () => {
    const allProgress = getAgentProgress();
    return allProgress.filter((a) => a.progress <= 50);
  };

  const handleDownloadAllAgents = () => {
    const allProgress = getAgentProgress();
    // Select fields to include in CSV
    const data = allProgress.map((a) => ({
      AgentCode: a.agentCode,
      Agent: a.agentName,
      Email: a.agentEmail,
      Address: a.agentAddress,
      Phone: a.agentPhone,
      "Status": a.agentStatus,
      "Total Students": a.totalStudents,
      "Recent Students": a.recentStudents,
      "Progress (%)": a.progress,
    }));

    downloadCSV(data, "all_agents.csv");
  }

  const handleDownloadLowProgress = () => {
    const lowAgents = getLowProgressAgents();

    // Select fields to include in CSV
    const data = lowAgents.map((a) => ({
      Agent: a.agentName,
      Email: a.agentEmail,
      Address: a.agentAddress,
      Phone: a.agentPhone,
      "Total Students": a.totalStudents,
      "Recent Students": a.recentStudents,
      "Progress (%)": a.progress,
    }));

    downloadCSV(data, "low_progress_agents.csv");
  };
  const handleSort = (key) => {
    setSortConfig((prev) => {
      // toggle direction if same column clicked
      const direction = prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const getStudentsStartingSoon = () => {
    const today = new Date();
    const threshold = 10; // next 10 days & last 10 days

    return prism.flatMap((student) => {
      const courses = student.formState.courses || [];

      return courses
        .map((course) => {
          if (!course.courseStartDate) return null;

          const startDate = new Date(course.courseStartDate);
          const diffDays = Math.round(
            (startDate - today) / (1000 * 60 * 60 * 24)
          );

          // Show if:
          // - Started within the last 10 days (diffDays < 0 but >= -10)
          // - OR starting within the next 10 days (diffDays >= 0 and <= 10)
          if (diffDays < -10 || diffDays > threshold) return null;

          // Label for upcoming or recent
          let label = "";
          if (diffDays > 0) {
            label = `Starts in ${diffDays} day${diffDays > 1 ? "s" : ""}`;
          } else if (diffDays === 0) {
            label = "Starts Today";
          } else {
            const ago = Math.abs(diffDays);
            label = `Started ${ago} day${ago > 1 ? "s" : ""} ago`;
          }

          return {
            id: student,
            studentId: student.formState.studentId,
            studentName: `${student.formState.firstName} ${student.formState.lastName}`,
            campus: student.formState.campusName || "N/A",
            courseName: course.courseName,
            studentStatus: student.formState.studentStatus || "N/A",
            diffDays,
            courseStartDate: `${course.courseStartDate} (${label})`,
            statusLabel: label,
          };
        })
        .filter(Boolean);
    }).sort((a, b) => a.diffDays - b.diffDays); // recent starts (negative) first
  };



  const getStudentsCompletingSoon = () => {
    const today = new Date();
    const threshold = 30;

    return prism.flatMap((student) => {
      const courses = student.formState.courses || [];

      return courses
        .map((course, index) => {
          if (!course.courseEndDate) return null;

          const endDate = new Date(course.courseEndDate);
          const daysLeft = Math.round(
            (endDate - today) / (1000 * 60 * 60 * 24)
          );

          if (daysLeft < 0 || daysLeft > threshold) return null;

          const nextCourse = courses[index + 1]?.courseName || "";

          return {
            id: student,
            studentId: student.formState.studentId,
            studentName: `${student.formState.firstName} ${student.formState.lastName}`,
            campus: student.formState.campusName || "N/A",
            courseName: course.courseName,
            courseStartDate: course.courseStartDate,
            daysLeft,  // <<< THIS is the key for sorting
            courseEndDate: `${course.courseEndDate} (${daysLeft} days left)`,
            nextCourse,
          };
        })
        .filter(Boolean);
    })
    .sort((a, b) => a.daysLeft - b.daysLeft);  // <<< SORT APPLIED HERE
  };



  // -----------------------------
  // CSV Download
  // -----------------------------
  const downloadCSV = (data, filename) => {
  if (!data || data.length === 0) return;
  let excludeFields = ["id"];

  // Get all headers, excluding the ones in excludeFields
  const headers = Object.keys(data[0]).filter((h) => !excludeFields.includes(h));

  const csvRows = [
    headers.join(","), // CSV header row
    ...data.map((row) =>
      headers
        .map((h) => {
          const value = row[h] != null ? row[h] : ""; // handle null/undefined
          return `"${String(value).replace(/"/g, '""')}"`; // escape quotes
        })
        .join(",")
    ),
  ];

  const blob = new Blob([csvRows.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.click();
};

  // -----------------------------
  // Cards
  // -----------------------------
  const cards = [
    {
      id: "enrolments",
      title: "New Enrolments",
      number: getNewEnrolments().length,
      icon: BookOpen,
      color: "var(--color-primary)",
    },
    {
      id: "enquiries",
      title: "New Enquiries",
      number: getNewEnquiries().length,
      icon: MessageCircle,
      color: "var(--color-primary)",
    },
    {
      id: "prism",
      title: "Prism Due",
      number: getPrismDueSoon().length,
      icon: Clock,
      color: "var(--color-primary)",
    },
    {
      id: "agents",
      title: "Agent Progress",
      number: 0,
      icon: BookOpen, // or replace with any relevant icon
      color: "var(--color-primary)",
    },
    {
      id: "fees",
      title: "Fee Installments Due",
      number: getFeeInstallmentsDue().length,
      icon: Clock,
      color: "var(--color-primary)",
    },
    {
      id: "starting",
      title: "Students Starting Soon",
      number: 0,
      icon: Clock,
      color: "var(--color-primary)",
    },
    {
      id: "completing",
      title: "Students Completing Soon",
      number: 0,
      icon: Clock,
      color: "var(--color-primary)",
    },
    

  ];

  // -----------------------------
  // Table rendering
  // -----------------------------
  const getDataForSelectedCard = () => {
    if (selectedRecord) {
      switch (selectedCard) {
        case "enrolments":
          return (
            <ViewEnrolmentForm
              initialData={selectedRecord}
              onCancel={() => setSelectedRecord(null)}
            />
          );
        case "enquiries":
          return (
            <EnquiryForm
              initialData={selectedRecord}
              onCancel={() => setSelectedRecord(null)}
            />
          );
        case "prism":
          return (
            <StudentRecordForm
              onCancel={() => setSelectedRecord(null)}
              initialData={selectedRecord.id}
            />
          );
        case "agents":
          return (
            <div>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedRecord(null)}
              >
                ← Back
              </button>
              <pre>{JSON.stringify(selectedRecord, null, 2)}</pre>
            </div>
          );
        case "completing":
          return (
            <ViewStudentRecordForm
                onCancel={() => setSelectedRecord(null)}
                initialData={selectedRecord.id}
            />
          );
        case "starting":
          return (
            <ViewStudentRecordForm
                onCancel={() => setSelectedRecord(null)}
                initialData={selectedRecord.id}
            />
          );
        case "fees":
          return (
            <ViewStudentRecordForm
                onCancel={() => setSelectedRecord(null)}
                initialData={selectedRecord.id}
            />
          );


        default:
          return null;
      }
    }

    switch (selectedCard) {
      case "enrolments":
        return (
          <table className="data-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Course</th>
                <th>Applied on</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {getNewEnrolments().map((row) => (
                <tr
                  key={row.id}
                  onClick={() => setSelectedRecord(row)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    {row.formState?.personal?.givenNames}{" "}{row.formState?.personal?.familyName}
                  </td>
                  <td>{row.formState?.personal?.gender}</td>
                  <td>{row.createdAt.toDate().toLocaleDateString()}</td>
                  <td>
                    <StatusBadge status={row.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "enquiries":
        return (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Agent</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {getNewEnquiries().map((row) => (
                <tr
                  key={row.id}
                  onClick={() => setSelectedRecord(row)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    {row.firstName} {row.lastName}
                  </td>
                  <td>{row.email}</td>
                  <td>{row.phone}</td>
                  <td>{row.agent}</td>
                  <td>
                    <StatusBadge status={row.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "prism":
        return (
          <table className="data-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Course Name</th>
                <th>Prism Due Date</th>
                <th>Prism Status</th>
              </tr>
            </thead>
            <tbody>
              {getPrismDueSoon().map((row, index) => {
                // extract number of days from prismsDueDate string "(3 days)"
                const match = row.prismsDueDate.match(/\((\-?\d+)\s+days?\)/);
                const days = match ? parseInt(match[1], 10) : null;

                // human-friendly label
                let daysLabel = "";
                if (days !== null) {
                  if (days === 0) {
                    daysLabel = "due today";
                  } else if (days > 0) {
                    daysLabel = `in ${days} day${days > 1 ? "s" : ""}`;
                  } else {
                    daysLabel = `${Math.abs(days)} day${Math.abs(days) > 1 ? "s" : ""} overdue`;
                  }
                }
                const isImmediate = days !== null && days <= 3; 
                return (
                  <tr
                    key={index}
                    onClick={() => setSelectedRecord(row)}
                    style={{ cursor: "pointer", backgroundColor: isImmediate ? "#f0d7d7ff" : "transparent" }}
                  >
                    <td>{row.studentName}</td>
                    <td>{row.courseName}</td>
                    <td>
                      {row.prismsDueDate.replace(/\s*\(.*\)/, "")}{" "}
                      {days !== null && (
                        <span
                          style={{
                            fontWeight: "bold",
                            color: isImmediate ? "red" : "green",
                            marginLeft: "5px",
                          }}
                        >
                          ({daysLabel})
                        </span>
                      )}
                      {isImmediate && (
                        <div
                          style={{
                            color: "black",
                            fontWeight: "bold",
                            fontSize: "0.9rem",
                            marginTop: "2px",
                          }}
                        >
                          ⚠️ Immediate Action Required
                        </div>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={row.prismStatus} />
                    </td>
                  </tr>
                );
              })}
            </tbody>


          </table>
        );
        case "agents":
          const sortedAgents = sortData(getAgentProgress());
          return (
            <table className="data-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort("agentName")}>Agent Name</th>
                  <th onClick={() => handleSort("agentEmail")}>Email</th>
                  <th onClick={() => handleSort("agentPhone")}>Phone</th>
                  <th onClick={() => handleSort("totalStudents")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Total Students <SortIcon direction={sortConfig.key === "totalStudents" ? sortConfig.direction : null} />
                  </th>
                  <th onClick={() => handleSort("recentStudents")}>Recent Students (in last 3 months)</th>
                  <th onClick={() => handleSort("progress")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Progress (in last 3 months) <SortIcon direction={sortConfig.key === "progress" ? sortConfig.direction : null} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedAgents.map((row, i) => (
                  <tr key={i}>
                    <td>{row.agentName}</td>
                    <td>{row.agentEmail}</td>
                    <td>{row.agentPhone}</td>
                    <td>{row.totalStudents}</td>
                    <td>{row.recentStudents}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div
                          style={{
                            background: "#e0e0e0",
                            width: "100px",
                            height: "6px",
                            borderRadius: "4px",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${row.progress}%`,
                              height: "100%",
                              background:
                                row.progress >= 75
                                  ? "#4caf50"
                                  : row.progress >= 50
                                  ? "#ffb300"
                                  : "#f44336",
                              transition: "width 0.3s ease",
                            }}
                          ></div>
                        </div>
                        <span>{row.progress}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
          case "starting":
          return (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Campus</th>
                  <th>Course</th>
                  <th>Start Date</th>
                  <th>Status</th>
                  <th>Student Status</th>
                </tr>
              </thead>

              <tbody>
                {getStudentsStartingSoon().map((row, index) => {
                  const color =
                    row.diffDays < 0 ? "#e0f7fa" :   // started recently (blue-ish)
                    row.diffDays <= 3 ? "#fff3cd" : // starting soon (yellow)
                    "transparent";

                  return (
                    <tr
                      key={index}
                      onClick={() => setSelectedRecord(row)}
                      style={{ cursor: "pointer", backgroundColor: color }}
                    >
                      <td>{row.studentName}</td>
                      <td>{row.campus}</td>
                      <td>{row.courseName}</td>
                      <td>{row.courseStartDate}</td>
                      <td><b>{row.statusLabel}</b></td>
                      <td>{row.studentStatus}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );


      case "completing":
        return (
          <table className="data-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Campus</th>
                <th>Course</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Next Course</th>
              </tr>
            </thead>

            <tbody>
              {getStudentsCompletingSoon().map((row, index) => (
                <tr
                  key={index}
                  onClick={() => setSelectedRecord(row)} // opens student form
                  style={{ cursor: "pointer" }}
                >
                  <td>{row.studentName}</td>
                  <td>{row.campus}</td>
                  <td>{row.courseName}</td>
                  <td>{row.courseStartDate}</td>
                  <td>{row.courseEndDate}</td>
                  <td>{row.nextCourse || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
     case "fees": {
        const allFeeData = getFeeInstallmentsDue();
        const pastDues = allFeeData.filter((row) => row.daysRemaining < 0);
        const futureDues = allFeeData.filter((row) => row.daysRemaining >= 0);
        const feeData = feeDueTab === "past" ? pastDues : futureDues;

        return (
          <div>
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                marginBottom: "1rem",
                borderBottom: "1px solid #ddd",
                paddingBottom: "0.5rem",
              }}
            >
              <button
                type="button"
                onClick={() => setFeeDueTab("past")}
                className={feeDueTab === "past" ? "btn btn-primary" : "btn btn-secondary"}
              >
                Past Dues ({pastDues.length})
              </button>

              <button
                type="button"
                onClick={() => setFeeDueTab("future")}
                className={feeDueTab === "future" ? "btn btn-primary" : "btn btn-secondary"}
              >
                Future Dues ({futureDues.length})
              </button>
            </div>

            {feeData.length === 0 && (
              <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                No {feeDueTab === "past" ? "past" : "future"} fee installments found.
              </div>
            )}

            {feeData.length > 0 && (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Campus</th>
                    <th>Type</th>
                    <th>Invoice Sent</th>
                    <th>Pending Balance</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {feeData
                    .sort((a, b) => {
                      if (feeDueTab === "past") {
                        return a.daysRemaining - b.daysRemaining;
                      }

                      return a.daysRemaining - b.daysRemaining;
                    })
                    .map((row, index) => {
                      const color =
                        row.daysRemaining < 0
                          ? "#ffe5e5"
                          : row.daysRemaining <= 7
                          ? "#fff2cc"
                          : "transparent";

                      return (
                        <tr
                          key={index}
                          onClick={() => {
                            if (row._studentRef) {
                              setSelectedRecord({
                                id: row._studentRef,
                                openSection: "feesPlan",
                              });
                            }
                          }}
                          style={{ cursor: "pointer", backgroundColor: color }}
                        >
                          <td>
                            <div>
                              <strong>{row.studentName}</strong>
                              <div style={{ fontSize: "0.85rem", color: "#666" }}>
                                {row.studentId}
                              </div>
                            </div>
                          </td>

                          <td>{row.campus}</td>

                          <td>
                            <span
                              style={{
                                padding: "2px 8px",
                                borderRadius: "4px",
                                fontSize: "0.85rem",
                                background:
                                  row.coeType === "Package" ? "#e3f2fd" : "#f3e5f5",
                                color:
                                  row.coeType === "Package" ? "#1565c0" : "#6a1b9a",
                              }}
                            >
                              {row.coeType}
                            </span>
                          </td>
                          <td>
                            <span
                              style={{
                                padding: "4px 10px",
                                borderRadius: "12px",
                                fontSize: "0.85rem",
                                fontWeight: "500",
                                background: row.invoiceSent ? "#e8f5e9" : "#ffebee",
                                color: row.invoiceSent ? "#2e7d32" : "#c62828",
                              }}
                            >
                              {row.invoiceSent ? "Sent" : "Not Sent"}
                            </span>

                            {row.invoiceSentDate && (
                              <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "2px" }}>
                                Invoice Sent on: {row.invoiceSentDate}
                              </div>
                            )}
                          </td>

                          <td
                            style={{
                              color: row.daysRemaining < 0 ? "#d32f2f" : "#e65100",
                              fontWeight: "bold",
                              fontSize: "1.05rem",
                            }}
                          >
                            ${row.pendingBalance.toFixed(2)}
                          </td>

                          <td>
                            <span
                              style={{
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "0.85rem",
                                fontWeight: "500",
                                background:
                                  row.daysRemaining < 0 ? "#ffebee" : "#fff3e0",
                                color:
                                  row.daysRemaining < 0 ? "#c62828" : "#e65100",
                              }}
                            >
                              {row.daysRemaining < 0
                                ? `⚠️ ${Math.abs(row.daysRemaining)} day${Math.abs(row.daysRemaining) > 1 ? "s" : ""} Overdue`
                                : row.daysRemaining === 0
                                ? "Due Today"
                                : `Due in ${row.daysRemaining} day${
                                    row.daysRemaining > 1 ? "s" : ""
                                  }`}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </div>
        );
      }

      default:
        return <p>Select a card to view details</p>;
    }
  };

  return (
    <div className="dashboard-report">
      <h1 className="dashboard-title">Dashboard Overview</h1>

      {/* Cards row */}
      <div className="cards-row">
        {cards.map((card) => {
          const IconComponent = card.icon;
          return (
            <div
              key={card.id}
              className={`card ${selectedCard === card.id ? "selected" : ""}`}
              onClick={() => {
                setSelectedCard(card.id);
                setSelectedRecord(null); // reset if switching tabs
              }}
            >
              <div className="card-header">
                <div
                  className="icon-wrapper"
                  style={{
                    color:
                      selectedCard === card.id ? "white" : "var(--color-primary)",
                  }}
                >
                  <IconComponent size={24} />
                </div>
                <h4>{card.title}</h4>
              </div>
              {card.number > 0 && (
                <div className="card-number-bubble">{card.number}</div>
              )}
            </div>
          );
        })}
      </div>

      {!selectedRecord && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <h2>{cards.find((c) => c.id === selectedCard)?.title}</h2>

          {selectedCard === "prism" && (
            <Download
              size={20}
              style={{ cursor: "pointer", color: "var(--color-primary)" }}
              onClick={() => {
                const dataMap = {
                  prism: getPrismDueSoon(),
                };
                const filenameMap = {
                  prism: "prism_due.csv",
                };
                downloadCSV(dataMap[selectedCard], filenameMap[selectedCard]);
              }}
            />
          )}
          {selectedCard === "agents" && (
            <div style={{ position: "relative" }}>
              <button
                title="Download"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer"
                }}
                onClick={() => setShowDownloadMenu((prev) => !prev)}
              >
                <Download size={20} style={{ color: "var(--color-primary)" }} />
              </button>

              {showDownloadMenu && (
                <div
                  style={{
                    position: "absolute",
                    top: "28px",
                    right: 0,
                    background: "white",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                    minWidth: "180px",
                    zIndex: 10
                  }}
                >
                  <div
                    onClick={() => {
                      handleDownloadLowProgress();
                      setShowDownloadMenu(false);
                    }}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      borderBottom: "1px solid #eee"
                    }}
                  >
                    Low progress agents
                  </div>

                  <div
                    onClick={() => {
                      handleDownloadAllAgents();
                      setShowDownloadMenu(false);
                    }}
                    style={{ padding: "8px 12px", cursor: "pointer" }}
                  >
                    All agents
                  </div>
                </div>
              )}
            </div>
          )}


        </div>
      )}


      {/* Table or Form */}
      <div className="table-container">{getDataForSelectedCard()}</div>
    </div>
  );
};

export default DashboardReport;
